import { containersApi, containersApiBlob } from './api';
import { refreshAccessToken } from '../helpers/ms-graph-api';

const baseRoute = '/items';

export const ItemAPI = {
  createNewItem: (item) => containersApi.post(baseRoute, item),
  saveItemEdits: (item) => containersApi.put(baseRoute, item),
  buildItemDetails: (item) =>
    containersApi.post(`${baseRoute}/buildItemDetails`, item),
  checkForDuplicates: (item) =>
    containersApi.post(`${baseRoute}/checkForDuplicates`, item),
  productGroups: () => containersApi.get(`${baseRoute}/productGroups`),
  getItemInventoryDetails: (itemId, orderNo) =>
    containersApi.get(
      `${baseRoute}/${itemId}/inventoryDetails?orderNo=${orderNo || ''}`
    ),
  getItemDetailsForItemSearch: (itemIds) =>
    containersApi.post(`${baseRoute}/details/search`, itemIds),
  attachPriceAndLocationDetailsToLine: (line) =>
    containersApi.post(
      `${baseRoute}/attachPriceAndLocationDetailsToLine`,
      line
    ),
  getCustomerPartNumbers: (customerId, itemId) =>
    containersApi.get(
      `${baseRoute}/customerPartNumbers?customerId=${customerId}&itemId=${itemId}`
    ),
  handleCustomerPartNumber: (
    customerId,
    itemId,
    customerPartNumber,
    deleteFlag
  ) =>
    containersApi.post(`${baseRoute}/customerPartNumber`, {
      customerId,
      itemId,
      customerPartNumber,
      deleteFlag,
    }),
  createPredefinedRoute: (predefinedRoute) =>
    containersApi.post(`${baseRoute}/predefinedRoutes`, predefinedRoute),
  getInventoryTransactions: ({
    itemId,
    locationId,
    transactionType,
    transactionReferenceNumber,
  }) =>
    containersApi.get(
      `${baseRoute}/inventoryTransactions?itemId=${itemId || ''}&locationId=${
        locationId || ''
      }&transactionType=${transactionType || ''}&transactionReferenceNumber=${
        transactionReferenceNumber || ''
      }`
    ),
  updateInventoryTransaction: (transaction) =>
    containersApi.put(
      `${baseRoute}/inventoryTransactions/${transaction.transactionId}`,
      transaction
    ),
  checkInventoryImpact: (pendingTransactions) =>
    containersApi.post(
      `${baseRoute}/checkInventoryImpact`,
      pendingTransactions
    ),
  getProductDetails: (itemUids) =>
    containersApi.post(`${baseRoute}/details`, itemUids),
  getProductPricing: (items) =>
    containersApi.post(`${baseRoute}/pricingInternal`, items),
  getItemNotes: (itemId) => containersApi.get(`${baseRoute}/${itemId}/notes`),
  lookupItems: ({ itemIds = [], searchString, searchByItemId }) =>
    containersApi.post(
      `${baseRoute}/lookup?searchString=${searchString || ''}&searchByItemId=${
        searchByItemId || ''
      }`,
      {
        itemIds,
      }
    ),
  getLabelTemplateUrl: ({
    itemId,
    templateType = 'label',
    labelType,
    height,
    width,
  }) =>
    containersApi.get(
      `/search/label?itemId=${itemId}&templateType=${templateType}&labelType=${labelType}&height=${height}&width=${width}`
    ),
  getDocumentLinks: ({ itemId }) =>
    containersApi.get(`${baseRoute}/${itemId}/documentLinks`),
  viewDocumentLinksFile: ({ itemId, rowIndex }) =>
    containersApiBlob.get(`${baseRoute}/${itemId}/documentLinks/${rowIndex}`),
  addDocumentLinks: ({ updatedDocumentLinks, newDocumentLinks, itemId }) =>
    containersApi.post(`${baseRoute}/${itemId}/documentLinks`, {
      updatedDocumentLinks,
      newDocumentLinks,
    }),
  getPurchaseOrderDetails: ({
    itemId,
    legacyLocationId,
    year,
    month,
    showCompleted,
  }) =>
    containersApi.get(`${baseRoute}/${itemId}/purchaseOrders`, {
      params: {
        legacyLocationId,
        year,
        month,
        showCompleted: showCompleted ? 'true' : undefined,
      },
    }),
  getProductActiveQuotes: (itemId) =>
    containersApi.get(`${baseRoute}/${itemId}/activeQuotes`),
  getCostItems: ({ itemIds, warehouseId, filterQueryOptions }) =>
    containersApi.get(`${baseRoute}/itemCostQueue`, {
      params: {
        itemIds,
        warehouseId,
        filterQueryOptions,
      },
    }),
  updateCostItems: ({ itemsToUpdate }) =>
    containersApi.put(`${baseRoute}/itemCost`, itemsToUpdate),
  getItemCustomerUsage: ({ itemID }) =>
    containersApi.get(`${baseRoute}/${itemID}/customerUsage`),
  getItemHistory: ({ itemID, year }) =>
    containersApi.get(`${baseRoute}/${itemID}/${year}/itemHistory`),
  getItemHistoryDetails: ({ itemID, year, month, historyType, locationId }) =>
    containersApi.get(
      `${baseRoute}/${itemID}/${year}/${month}/${historyType}/${locationId}/itemHistoryDetail`
    ),
  getItemHistorySHSF: ({ itemId, year, month, locationId }) =>
    containersApi.get(
      `${baseRoute}/${itemId}/${year}/${month}/${locationId}/shsf`
    ),
  // Streams the AI-sourcing response as newline-delimited JSON. axios can't
  // read a streaming response body in the browser, so this uses fetch directly
  // and attaches the same MSAL Bearer token the axios interceptor would.
  // `onEvent` is called once per SearchProgressEvent as it arrives; pass an
  // AbortSignal to cancel an in-flight request. Resolves when the stream ends.
  askAiStream: async (
    { query, conversationId, archiveId, images },
    { onEvent, signal } = {}
  ) => {
    const token = await refreshAccessToken();
    const res = await fetch(`${process.env.NEXT_PUBLIC_CONTAINERS_API}${baseRoute}/ai`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        query,
        ...(conversationId && { conversationId }),
        ...(archiveId && { archiveId }),
        ...(images?.length && { images }),
      }),
    });

    // Errors raised before streaming begins (e.g. 401 from checkTokenInternal)
    // arrive as a normal non-OK response, not as a stream event.
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `AI request failed (${res.status})`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const flushLine = (line) => {
      const trimmed = line.trim();
      if (trimmed) onEvent?.(JSON.parse(trimmed));
    };

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl;
      while ((nl = buffer.indexOf('\n')) >= 0) {
        flushLine(buffer.slice(0, nl));
        buffer = buffer.slice(nl + 1);
      }
    }
    flushLine(buffer); // trailing line without a newline, if any
  },
  getAiUsage: () => containersApi.get('/ai/usage'),
  getUnshippableItems: () => containersApi.get(`${baseRoute}/unshippableItems`),
  getMissedOpportunities: ({ itemId }) =>
    containersApi.get(`${baseRoute}/${itemId}/missed-opportunities`),
  createMissedOpportunity: ({
    itemId,
    customerId,
    quantity,
    warehouses,
    existingBusiness,
  }) =>
    containersApi.post(`${baseRoute}/${itemId}/missed-opportunities`, {
      customerId,
      quantity,
      warehouses,
      existingBusiness,
    }),
};
